import { LockIcon } from '@f8n/icons';
import { styled } from '@f8n-frontend/stitches';
import { useWriteContract, useSimulateContract } from 'wagmi';

import TransactionModal from 'components/TransactionModal';
import TransactionModalContent from 'components/TransactionModalContent';
import Link from 'components/base/Link';
import Message from 'components/base/Message';
import Modal from 'components/base/Modal';
import Skeleton from 'components/base/Skeleton';
import Text from 'components/base/Text';
import Tooltip from 'components/base/Tooltip';
import { hasPublicKey } from 'contexts/auth/helpers';
import useAuth from 'contexts/auth/useAuth';
import useTransactionStore from 'state/stores/transactions';

import useMadeOffersCount from 'hooks/queries/hasura/markets/use-made-offers-count';
import useModalVisibility from 'hooks/use-modal-visibility';
import useMarketBalances from 'hooks/web3/use-market-balances';
import { FETH } from 'lib/abis/FETH';
import { getContractAddress } from 'lib/addresses';
import { ENV } from 'utils/env';
import { formatBigIntEthWithSuffix } from 'utils/formatters';
import { isNonZero } from 'utils/numbers';
import { extractPrepareContractWriteRevertReason } from 'utils/revert-reasons';
import { helpCenterPaths } from 'utils/router';

const MODAL_KEY = 'MARKETPLACE_BALANCE';
const ACTION = 'withdraw-feth';

export default function MarketplaceBalance() {
  const modal = useModalVisibility(MODAL_KEY);

  return (
    <Modal.Root open={modal.open} onOpenChange={modal.onOpenChange}>
      <Modal.Portal>
        <Modal.BlurOverlay />
        <Modal.PositionOverlay>
          <Modal.UnmountListener onUnmount={modal.onUnmount} />
          {modal.config && <MarketplaceBalanceWindow />}
        </Modal.PositionOverlay>
      </Modal.Portal>
    </Modal.Root>
  );
}

function MarketplaceBalanceWindow() {
  const auth = useAuth();
  const txStore = useTransactionStore();

  const chainId = hasPublicKey(auth) ? auth.chainId : ENV.PRIMARY_CHAIN_ID;

  const marketBalancesQuery = useMarketBalances({
    chainId,
  });

  const madeOffersCount = useMadeOffersCount({
    publicKey: hasPublicKey(auth) ? auth.publicKey : '',
  });

  const offersCount = madeOffersCount.data || 0;
  const fethBalance = marketBalancesQuery.data
    ? marketBalancesQuery.data.availableFethBalance
    : null;
  const lockedFethBalance = marketBalancesQuery.data
    ? marketBalancesQuery.data.lockedFethBalance
    : null;
  const formattedFethBalance = fethBalance
    ? formatBigIntEthWithSuffix(fethBalance)
    : null;

  const simulateWithdrawFethBalance = useSimulateContract({
    abi: FETH,
    functionName: 'withdrawAvailableBalance',
    address: getContractAddress({
      chainId,
      contractName: 'feth',
    }),
    chainId,
    account: hasPublicKey(auth) ? auth.publicKey : undefined,
    query: {
      retry: false,
    },
  });

  const contractWrite = useWriteContract({
    mutation: {
      onSuccess: (txHash) => {
        txStore.startTracking({
          chainId,
          ui: 'toast',
          action: ACTION,
          txHash,
          title: {
            PENDING: 'Converting balance…',
            SUCCESS: 'Balance converted',
          },
          description: {
            PENDING: 'Your Marketplace Balance is being converted into ETH.',
            SUCCESS: formattedFethBalance
              ? `Marketplace Balance was successfully converted. The full amount of ${formattedFethBalance} is now in your wallet.`
              : 'Marketplace Balance was successfully converted.',
          },
        });
      },
    },
  });

  const parsedError = extractPrepareContractWriteRevertReason(
    simulateWithdrawFethBalance
  );

  const getFooter = () => {
    if (fethBalance && isNonZero(fethBalance)) {
      return (
        <Modal.Footer>
          <TransactionModal.TransactionButton
            chainId={chainId}
            isLoading={contractWrite.isPending || Boolean(contractWrite.data)}
            isDisabled={!simulateWithdrawFethBalance.isSuccess}
            write={() => {
              if (simulateWithdrawFethBalance.isSuccess) {
                contractWrite.writeContract(
                  simulateWithdrawFethBalance.data.request
                );
              }
            }}
            error={parsedError}
            label="Convert"
          />
        </Modal.Footer>
      );
    }
    return null;
  };

  return (
    <TransactionModal
      chainId={chainId}
      preSignPrompt={{
        type: 'skip',
      }}
    >
      <TransactionModalContent
        action={ACTION}
        chainId={chainId}
        txHash={contractWrite.data || null}
        footer={getFooter()}
      >
        <TransactionModal.Body css={{ gap: '$6' }}>
          <Modal.BodyTitle
            align="left"
            title="Marketplace Balance"
            description="When your Offer expires or a higher Offer is made, you are auto-refunded to your Marketplace Balance."
          />
          <BalancesContainer>
            <Text weight="semibold" color="dim" lineHeight={2}>
              Marketplace Balance
            </Text>
            {marketBalancesQuery.isLoading ? (
              <Skeleton.Block
                css={{
                  width: 150,
                  height: 32,
                  marginTop: '$3',
                  marginBottom: '$1',
                }}
              />
            ) : (
              <Text weight="semibold" size={5} lineHeight={2}>
                {formattedFethBalance}
              </Text>
            )}
            {lockedFethBalance !== null && isNonZero(lockedFethBalance) && (
              <OffersContainer>
                <ActiveOffersLink
                  color="dim"
                  size={1}
                  variant="primary"
                  weight="semibold"
                  as="div"
                >
                  Active Offers
                  <Count>{offersCount}</Count>
                </ActiveOffersLink>
                <Tooltip content="Unlocks after 24hrs">
                  <LockedBalanceContainer>
                    <LockIcon size={0} />
                    <Text color="strong" size={1} weight="semibold">
                      {formatBigIntEthWithSuffix(lockedFethBalance)}
                    </Text>
                  </LockedBalanceContainer>
                </Tooltip>
              </OffersContainer>
            )}
          </BalancesContainer>
          <Message
            message={
              <>
                You can use Marketplace Balance to make another Offer, purchase
                a work through Buy Now, place a Bid, or convert your balance
                back to ETH.{' '}
                <Link
                  target="_blank"
                  rel="noreferrer"
                  href={helpCenterPaths.marketplaceBalance}
                  color="dim"
                  hasUnderline
                >
                  Learn about Marketplace Balance
                </Link>
              </>
            }
          />
        </TransactionModal.Body>
      </TransactionModalContent>
    </TransactionModal>
  );
}

const ActiveOffersLink = styled(Link, {
  gap: '6px',
  display: 'flex',
  alignItems: 'center',
  textDecoration: 'none',
});

const BalancesContainer = styled('div', {
  paddingX: '$6',
  paddingTop: '$5',
  borderRadius: '$3',
  paddingBottom: '$4',
  border: '1px solid $black10',
});

const OffersContainer = styled('div', {
  display: 'flex',
  marginTop: '$3',
  paddingTop: '$5',
  paddingBottom: '$1',
  borderTop: '1px solid $black10',
  justifyContent: 'space-between',
});

const LockedBalanceContainer = styled('div', {
  gap: '$1',
  display: 'flex',
  alignItems: 'center',
  fontWeight: '$semibold',
});

const Count = styled('div', {
  display: 'flex',
  alignItems: 'center',
  borderRadius: '$round',
  justifyContent: 'center',
  backgroundColor: '$black10',

  height: '$icon2',
  minWidth: '$icon2',

  fontSize: '$0',
  color: '$black70',
  textAlign: 'center',
  fontWeight: '$semibold',
});
